import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import DaySelector from "./DaySelector";
import SportFilter from "./SportFilter";
import SortingControl from "./SortingControl";
import DisplayOptions from "./DisplayOptions";
import FilterIndicator from "./FilterIndicator";
import LeagueList from "./LeagueList";
import { leaguesData } from "../data";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSelectedLeague,
  setLeagues,
  setSelectedLeague,
} from "../../../store/league-slice";
import { transformApiData } from "../../../utils";
import { useGetGamesQuery } from "../../../store/apis";
import SportBetSkeleton from "./SportBetSkeleton";
import {
  selectSelectedCountry,
  selectSelectedCountryLeague,
  setCountries,
} from "../../../store/country-slice";
import { selectFixtures, setInitialOdds } from "../../../store/odd-slice";
import { useTheme } from "../../../context/ThemeContext";
import HighlightSection from "./HighlightSection";

const SportBet = () => {
  // State for filters and display
  // const [isDarkMode, setIsDarkMode] = useState(false);
  const { isDarkMode } = useTheme();
  const [mobileDisplayOption, setMobileDisplayOption] = useState("gg-ng");
  const [selectedDay, setSelectedDay] = useState("TODAY");
  const [selectedHour, setSelectedHour] = useState(null);
  const [sortOption, setSortOption] = useState("league");
  const [expandedLeagues, setExpandedLeagues] = useState({});
  const [selectedSport, setSelectedSport] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedOdd, setSelectedOdd] = useState(
    leaguesData[0]?.titleWrps[0]?.matches[0]?.odds[0][0] || null
  );
  const { data, isLoading, isFetching } = useGetGamesQuery();
  const [transformedData, setTransformedData] = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const itemsPerPage = 10; // Number of leagues to load per page
  const observerRef = useRef(null);

  const selectedLeague = useSelector(selectSelectedLeague);
  const selectedCountryLeague = useSelector(selectSelectedCountryLeague);
  const selectedCountry = useSelector(selectSelectedCountry);
  const fixtures = useSelector(selectFixtures);
  const dispatch = useDispatch();
  const [activeHighlightSection, setActiveHighlightSection] =
    useState("highlights");

  // Add this handler function in your SportBet component
  const handleHighlightSectionChange = (sectionId) => {
    setActiveHighlightSection(sectionId);

    // TODO: For example, filter data or update other states
  };
  // Log fixtures data to verify we're receiving it correctly
  // useEffect(() => {
  //   console.log("Fixtures from store:", fixtures);
  // }, [fixtures]);

  const extractLeaguesFromData = (data) => {
    if (!data) return [];

    const seen = new Set();

    const uniqueLeagues = data.filter((league) => {
      if (seen.has(league.leagueTitle)) {
        return false;
      }
      seen.add(league.leagueTitle);
      return true;
    });
    return uniqueLeagues.map((league) => ({
      name: league.leagueTitle,
      country: league.leagueTitle.split(" - ")[0] || "",
      count: league.titleWrps.reduce(
        (total, wrap) => total + wrap.matches.length,
        0
      ),
    }));
  };

  // Function to extract unique countries and count games for each
  function getCountryGameCounts(apiData) {
    // Use reduce to build up the country counts
    const countryCounts = apiData.reduce((acc, game) => {
      const country = game.country;

      // If country already exists in accumulator, increment count
      // Otherwise, initialize with count of 1
      acc[country] = (acc[country] || 0) + 1;

      return acc;
    }, {});

    // Convert object to array of objects with country and count properties
    return Object.entries(countryCounts).map(([country, count]) => ({
      country: country,
      count: count,
    }));
  }

  useEffect(() => {
    if (data) {
      dispatch(setInitialOdds(data));
      const transformedData = transformApiData(data);
      setTransformedData(transformedData);
      const extractedLeagues = extractLeaguesFromData(transformedData);
      const extractedCountries = getCountryGameCounts(transformedData);

      // dispatch(setLeagues(extractedLeagues));
      // dispatch(setCountries(extractedCountries));

      // Reset pagination
      setPage(1);
      setHasMore(true);
    }
  }, [data, dispatch]);

  // Enhanced function to incorporate fixtures data from Redux store
  // Enhanced filtering function with improved logic for all filters
  const getFilteredAndSortedData = useCallback(() => {
    if (!transformedData || transformedData.length === 0) return [];

    let filteredData = [...transformedData];

    // 1. Filter by selected league (explicitly chosen by user)
    if (selectedLeague && selectedLeague.isExplicitlySelected) {
      filteredData = filteredData.filter(
        (league) =>
          league.leagueId === selectedLeague.league_id ||
          league.leagueTitle === selectedLeague.name
      );
    }

    // 2. Filter by selected country league
    if (selectedCountryLeague) {
      filteredData = filteredData.filter(
        (league) =>
          league.leagueId === selectedCountryLeague.leagueId ||
          league.country === selectedCountryLeague.country
      );
    }

    // 3. Filter by selected sport/category
    if (selectedSport) {
      filteredData = filteredData.filter((league) => {
        // Check if sport name is included in league title (case-insensitive)
        return (
          league.leagueTitle
            .toLowerCase()
            .includes(selectedSport.toLowerCase()) ||
          league.country.toLowerCase().includes(selectedSport.toLowerCase())
        );
      });
    }

    // 4. Filter by selected day/date
    if (selectedDay && selectedDay !== "TODAY") {
      const targetDate =
        selectedDay === "TOMORROW"
          ? new Date(Date.now() + 24 * 60 * 60 * 1000)
          : selectedDate;

      filteredData = filteredData.filter((league) => {
        return league.titleWrps.some((wrp) =>
          wrp.matches.some((match) => {
            if (match.eventDate) {
              const matchDate = new Date(match.eventDate);
              return isSameDay(matchDate, targetDate);
            }
            return false;
          })
        );
      });
    }

    // 5. Filter by selected hour
    if (selectedHour) {
      const hourPrefix = selectedHour.split(" ")[0]; // Extract hour part (e.g., "14" from "14:30")

      filteredData = filteredData.filter((league) => {
        return league.titleWrps.some((wrp) =>
          wrp.matches.some((match) => {
            // Check in match details for time
            const hasTimeInDetails = match.details.some((detail) =>
              detail.text.includes(hourPrefix)
            );

            // Also check eventDate if available
            let hasTimeInEventDate = false;
            if (match.eventDate) {
              const matchTime = new Date(match.eventDate).toLocaleTimeString(
                "en-US",
                {
                  hour12: false,
                  hour: "2-digit",
                }
              );
              hasTimeInEventDate = matchTime.startsWith(hourPrefix);
            }

            return hasTimeInDetails || hasTimeInEventDate;
          })
        );
      });
    }

    // 6. Filter leagues that have matches after applying match-level filters
    filteredData = filteredData
      .map((league) => {
        let filteredMatches = [...league.titleWrps[0].matches];

        // Apply match-level date filtering if needed
        if (selectedDay && selectedDay !== "TODAY") {
          const targetDate =
            selectedDay === "TOMORROW"
              ? new Date(Date.now() + 24 * 60 * 60 * 1000)
              : selectedDate;

          filteredMatches = filteredMatches.filter((match) => {
            if (match.eventDate) {
              const matchDate = new Date(match.eventDate);
              return isSameDay(matchDate, targetDate);
            }
            return false;
          });
        }

        // Apply match-level hour filtering if needed
        if (selectedHour) {
          const hourPrefix = selectedHour.split(" ")[0];
          filteredMatches = filteredMatches.filter((match) => {
            const hasTimeInDetails = match.details.some((detail) =>
              detail.text.includes(hourPrefix)
            );

            let hasTimeInEventDate = false;
            if (match.eventDate) {
              const matchTime = new Date(match.eventDate).toLocaleTimeString(
                "en-US",
                {
                  hour12: false,
                  hour: "2-digit",
                }
              );
              hasTimeInEventDate = matchTime.startsWith(hourPrefix);
            }

            return hasTimeInDetails || hasTimeInEventDate;
          });
        }

        return {
          ...league,
          titleWrps: [
            {
              ...league.titleWrps[0],
              matches: filteredMatches,
            },
          ],
        };
      })
      .filter((league) => league.titleWrps[0].matches.length > 0); // Remove leagues with no matches

    // 7. Use fixtures data for odds if available
    // if (fixtures && Object.keys(fixtures).length > 0) {
    //   filteredData = filteredData.map((league) => {
    //     return {
    //       ...league,
    //       titleWrps: league.titleWrps.map((wrap) => {
    //         return {
    //           ...wrap,
    //           matches: wrap.matches.map((match) => {
    //             // Find the fixture in Redux store by gameId
    //             const fixture = fixtures[match.gameId];
    //             if (fixture) {
    //               // Replace odds with data from store
    //               return {
    //                 ...match,
    //                 storeOdds: fixture.odds, // Add store odds for reference
    //                 odds: fixture.odds || match.odds, // Use store odds or fallback to original
    //               };
    //             }
    //             return match;
    //           }),
    //         };
    //       }),
    //     };
    //   });
    // }

    // 8. Sort the data
    if (sortOption === "time") {
      filteredData.sort((a, b) => {
        const getEarliestTime = (league) => {
          let earliestTime = "";
          for (const wrap of league.titleWrps) {
            for (const match of wrap.matches) {
              // Try to get time from eventDate first
              if (match.eventDate) {
                const timeFromDate = new Date(
                  match.eventDate
                ).toLocaleTimeString("en-US", {
                  hour12: false,
                });
                if (!earliestTime || timeFromDate < earliestTime) {
                  earliestTime = timeFromDate;
                }
              } else {
                // Fallback to details
                const timeDetail = match.details.find((d) =>
                  d.text.includes(":")
                );
                if (
                  timeDetail &&
                  (!earliestTime || timeDetail.text < earliestTime)
                ) {
                  earliestTime = timeDetail.text;
                }
              }
            }
          }
          return earliestTime;
        };

        const timeA = getEarliestTime(a);
        const timeB = getEarliestTime(b);
        return timeA.localeCompare(timeB);
      });
    } else if (sortOption === "league") {
      filteredData.sort((a, b) => a.leagueTitle.localeCompare(b.leagueTitle));
    } else if (sortOption === "country") {
      filteredData.sort((a, b) => {
        const countryA = a.country || a.leagueTitle.split(" - ")[0] || "";
        const countryB = b.country || b.leagueTitle.split(" - ")[0] || "";
        return countryA.localeCompare(countryB);
      });
    }

    return filteredData;
  }, [
    transformedData,
    selectedLeague,
    selectedCountryLeague,
    selectedSport,
    selectedHour,
    selectedDay,
    selectedDate,
    fixtures,
    sortOption,
  ]);
  useEffect(() => {
    console.log("filteredAndSortedData:", getFilteredAndSortedData());
    console.log("displayData:", displayData);
  }, [displayData, getFilteredAndSortedData]);
  // Helper function to check if two dates are the same day
  const isSameDay = (date1, date2) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  // Effect for lazy loading
  useEffect(() => {
    const filteredData = getFilteredAndSortedData();
    const end = page * itemsPerPage;
    const paginatedData = filteredData.slice(0, end);

    setDisplayData(paginatedData);
    setHasMore(paginatedData.length < filteredData.length);
  }, [page, getFilteredAndSortedData]); // ✅ Removed displayData from dependencies

  // Intersection observer for infinite scrolling
  const lastLeagueElementRef = useCallback(
    (node) => {
      if (isLoading) return;

      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isLoading, hasMore]
  );

  const handleLeagueSelect = (league) => {
    // Mark league as explicitly selected
    dispatch(setSelectedLeague({ ...league, isExplicitlySelected: true }));
  };

  // Helper function to determine which odds groups to display based on market names
  const getOddsGroup = (match, columnIndex) => {
    const isMobile = window.matchMedia("(max-width: 767px)").matches;

    // Safety check for match odds
    if (!match.odds || !Array.isArray(match.odds)) {
      return null;
    }

    // Define market name mappings
    const marketMappings = {
      "1x2": [
        "match result",
        "1x2",
        "full time result",
        "winner",
        "match winner",
      ],
      "double-chance": ["double chance", "double_chance", "dc"],
      "gg-ng": [
        "both teams to score",
        "btts",
        "gg/ng",
        "gg-ng",
        "goals goals",
        "both teams score",
      ],
      "over-under": [
        "over/under 2.5",
        "total goals",
        "over/under",
        "over-under",
        "goals over/under",
        "total",
        "o/u",
      ],
    };

    // Function to check if market matches a category
    const isMarketType = (marketName, category) => {
      const normalizedMarketName = marketName.toLowerCase().trim();
      return marketMappings[category].some(
        (mapping) =>
          normalizedMarketName.includes(mapping) ||
          mapping.includes(normalizedMarketName)
      );
    };

    // Find the market for each column position
    const findMarketForColumn = (columnIndex) => {
      if (isMobile) {
        // Mobile: Only show selected market type
        const targetMarketType = mobileDisplayOption;

        for (const group of match.odds) {
          if (Array.isArray(group) && group.length > 0) {
            const marketName = group[0]?.market || group[0]?.marketName || "";
            if (isMarketType(marketName, targetMarketType)) {
              return group;
            }
          }
        }
        return null;
      } else {
        // Desktop: Fixed column positions
        const columnMarkets = ["1x2", "double-chance"];

        // Add third market based on selection
        if (mobileDisplayOption === "gg-ng") {
          columnMarkets.push("gg-ng");
        } else {
          columnMarkets.push("over-under");
        }

        const targetMarketType = columnMarkets[columnIndex];
        if (!targetMarketType) return null;

        // Find the group that matches this column's market type
        for (const group of match.odds) {
          if (Array.isArray(group) && group.length > 0) {
            const marketName = group[0]?.market || group[0]?.marketName || "";
            if (isMarketType(marketName, targetMarketType)) {
              return group;
            }
          }
        }
        return null;
      }
    };

    return findMarketForColumn(columnIndex);
  };

  // Reset all filters
  const clearAllFilters = () => {
    setSelectedDay("TODAY");
    setSelectedHour(null);
    setSelectedSport(null);

    // Clear explicit league selection
    if (selectedLeague) {
      dispatch(
        setSelectedLeague({ ...selectedLeague, isExplicitlySelected: false })
      );
    }
  };

  // Show skeleton loader while fetching data
  if (isLoading || isFetching) {
    return <SportBetSkeleton isDarkMode={isDarkMode} />;
  }

  return (
    <div className="max-sm:mt-[5px] mt-3 s18:mt-[17px] rounded-[7px] bg-white dark:bg-bluedarkmodecolor shadow-dashboardshadow">
      <div className="sticky top-10 sm:top-12 md:top-20 xl:top-16 s15:top-16 s18:top-20 rounded-md bg-white dark:bg-bluedarkmode shadow-dashboardshadow">
        {/* NEW: Highlight Section - now includes Sports title on mobile */}
        <HighlightSection
          isDarkMode={isDarkMode}
          onSectionChange={handleHighlightSectionChange}
        />

        {/* Sports title and DaySelector - hidden on mobile, visible on desktop */}
        <div className="max-sm:flex-col max-sm:items-start max-sm:p-[15px_20px_0px] flex p-[0px_20px] items-center justify-between border-b-[1px] border-solid border-extralightblue dark:border-lightborderyellow">
          <span className="hidden md:flex text-mutedblue dark:text-white text-[18px] s18:text-[20px] font-bold leading-[160%]">
            Sports
          </span>

          {/* Day Selector Component */}
          <DaySelector
            isDarkMode={isDarkMode}
            selectedDay={selectedDay}
            setSelectedDay={setSelectedDay}
            setSelectedHour={setSelectedHour}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        </div>

        {/* Sport Filter Component */}
        <SportFilter
          isDarkMode={isDarkMode}
          selectedSport={selectedSport}
          setSelectedSport={setSelectedSport}
        />
      </div>

      {/* Sort and Display Options */}
      <div className="scrollable">
        <div className="max-md:min-w-full max-lg:min-w-[732px] flex items-center justify-between max-sm:p-[10px_15px] py-3 px-5 s18:p-[15px_23px]">
          <SortingControl
            isDarkMode={isDarkMode}
            sortOption={sortOption}
            setSortOption={setSortOption}
          />

          <DisplayOptions
            isDarkMode={isDarkMode}
            mobileDisplayOption={mobileDisplayOption}
            setMobileDisplayOption={setMobileDisplayOption}
          />
        </div>
      </div>

      {/* Filter Indicators */}
      {(selectedHour ||
        selectedDay !== "TODAY" ||
        selectedSport ||
        (selectedLeague && selectedLeague.isExplicitlySelected)) && (
        <FilterIndicator
          selectedDay={selectedDay}
          setSelectedDay={setSelectedDay}
          selectedHour={selectedHour}
          setSelectedHour={setSelectedHour}
          selectedSport={selectedSport}
          setSelectedSport={setSelectedSport}
          selectedLeague={
            selectedLeague && selectedLeague.isExplicitlySelected
              ? selectedLeague
              : null
          }
          setSelectedLeague={(league) =>
            dispatch(
              setSelectedLeague({ ...league, isExplicitlySelected: true })
            )
          }
          clearAllFilters={clearAllFilters}
        />
      )}

      {/* Stats indicator for fixtures from store */}
      {/* {fixtures && Object.keys(fixtures).length > 0 && (
        <div className="p-2 text-sm text-center text-mutedblue dark:text-white">
          {Object.keys(fixtures).length} fixtures loaded from store
        </div>
      )} */}

      {/* League List Component with Lazy Loading */}
      {displayData.length > 0 ? (
        <>
          <LeagueList
            isDarkMode={isDarkMode}
            filteredAndSortedData={displayData}
            expandedLeagues={expandedLeagues}
            setExpandedLeagues={setExpandedLeagues}
            getOddsGroup={getOddsGroup}
            selectedOdd={selectedOdd}
            mobileDisplayOption={mobileDisplayOption}
            setSelectedOdd={setSelectedOdd}
            lastLeagueElementRef={lastLeagueElementRef}
            fixturesFromStore={fixtures} // Pass fixtures from store
          />
          {isLoading && <SportBetSkeleton />}
          {!hasMore && displayData.length > itemsPerPage && (
            <div className="p-4 text-center text-mutedblue dark:text-white">
              All matches loaded
            </div>
          )}
        </>
      ) : (
        <div className="p-6 text-center text-mutedblue dark:text-white">
          No matches found for the selected filters.
        </div>
      )}
    </div>
  );
};

export default SportBet;
